/**
 * "AI กำลังคิดคำตอบ / AI กำลังพิมพ์" for a chat the AI is in charge of (owner 26 ก.ย. 69: "เวลา AI กำลังตอบให้มีสัญญาณว่า
 * AI กำลังคิด พนักงานจะได้รู้"). Pure rules, kept here so they are unit-tested; stores/aimode.ts holds the map.
 *
 * Live events (WS ai.busy from api/app/ai/aimode_runner.py) win over the page-load value (ConversationSummary
 * ai_mode.busy). Two API instances may both say "thinking" for the same customer message; `trigger` (that message's
 * id) orders them: an older message's event never overrides a newer one, and "thinking" never follows "typing" of
 * the same message. A state lives `ttl` seconds (the server says 180) if "idle" never arrives.
 */
import type { AiBusy } from '../api/types'

export interface BusyLive {
  state: AiBusy | 'idle'
  trigger: number
  /** local time the state stops counting (ms) */
  until: number
  /** idle right after typing: keep "กำลังพิมพ์" on screen until the bubble lands (or this time), no flicker */
  holdUntil: number
  seconds: number | null
  left: number | null
  /** the bubble of the current "typing" already landed (it can come before or after "idle") */
  landed?: boolean
}

/** How long an "idle" overrides the page-load value (the next summary from the server has caught up by then). */
export const IDLE_KEEP_MS = 60_000
/** "idle" may arrive a moment before the last bubble (different NOTIFY channels): keep the row this long at most. */
export const HOLD_MS = 2500

export interface BusyEvent {
  state: AiBusy | 'idle'
  trigger_id: string
  ttl: number
  seconds?: number
  left?: number
}

/** The new live entry after an event, or `prev` unchanged when the event is out of order. */
export function applyBusy(prev: BusyLive | undefined, e: BusyEvent, now: number): BusyLive | undefined {
  const trigger = Number(e.trigger_id) || 0
  if (prev && now < prev.until) {
    if (trigger < prev.trigger) return prev // an older message's news
    if (trigger === prev.trigger && e.state === 'thinking' && (prev.state === 'typing' || prev.state === 'idle')) return prev
  }
  if (e.state === 'idle') {
    const waiting = !!prev && now < prev.until && prev.state === 'typing' && prev.trigger === trigger && !prev.landed
    return { state: 'idle', trigger, until: now + IDLE_KEEP_MS, holdUntil: waiting ? now + HOLD_MS : 0, seconds: null, left: null }
  }
  return {
    state: e.state,
    trigger,
    until: now + Math.max(5, Math.min(600, e.ttl || 180)) * 1000,
    holdUntil: 0,
    seconds: e.seconds ?? null,
    left: e.left ?? null,
  }
}

/** What to show now: live state if still counting, else the page-load value. null = nothing. */
export function busyNow(live: BusyLive | undefined, pageLoad: AiBusy | null | undefined, now: number): AiBusy | null {
  if (live && now < live.until) {
    if (live.state === 'idle') return now < live.holdUntil ? 'typing' : null
    return live.state
  }
  return pageLoad ?? null
}

/** The AI's bubble arrived: an "idle" that was holding "กำลังพิมพ์" lets go now; during "typing" it is remembered
 *  so the "idle" that follows does not hold. */
export function bubbleLanded(live: BusyLive | undefined): BusyLive | undefined {
  if (live && live.state === 'idle' && live.holdUntil) return { ...live, holdUntil: 0 }
  if (live && live.state === 'typing' && !live.landed) return { ...live, landed: true }
  return live
}

export const BUSY_TEXT: Record<AiBusy, string> = {
  thinking: 'AI กำลังคิดคำตอบ',
  typing: 'AI กำลังพิมพ์ · จะส่งในไม่กี่วินาที',
}
export const BUSY_SHORT: Record<AiBusy, string> = {
  thinking: 'AI กำลังคิดคำตอบ…',
  typing: 'AI กำลังพิมพ์…',
}

/**
 * What a row of the AI tab says (owner 27 ก.ย. 69 "เพิ่ม AI = AI ตอบ กำลังทำงาน"), in plain Thai. A chat the AI handed
 * back is no longer in the AI tab (it waits in กำลังคุย › ต้องตอบ with the "AI ส่งต่อ" chip).
 *   thinking / typing  live (WS ai.busy) or the page-load value
 *   pending            the customer wrote and the AI has not started yet (it answers within seconds; after 3 min
 *                      without an answer the runner hands the chat back — docs/ai-mode.md "ตัวกวาด")
 *   answered           the AI's bubble is the last one: waiting for the customer
 *   watching           anything else (e.g. just turned on, nothing to answer yet)
 */
export type AiRowState = AiBusy | 'pending' | 'answered' | 'watching'

export const AI_ROW_LABEL: Record<AiRowState, string> = {
  thinking: 'AI กำลังคิดคำตอบ',
  typing: 'AI กำลังพิมพ์ตอบ',
  pending: 'ลูกค้ารอ AI ตอบ',
  answered: 'AI ตอบแล้ว · รอลูกค้า',
  watching: 'AI ดูแลอยู่',
}

export function aiRowState(
  c: { needs_reply: boolean; last_message: { direction: 'in' | 'out' } | null },
  busy: AiBusy | null,
): AiRowState {
  if (busy) return busy
  if (c.needs_reply) return 'pending'
  return c.last_message?.direction === 'out' ? 'answered' : 'watching'
}
